import React, { useState } from "react";
import SwitchIcon from "src/assets/images/switch.svg";

interface QuantIngestionPanelProps {
    json_response: any;
}

const QuantIngestionPanel = ({ json_response }: QuantIngestionPanelProps) => {
    const [open, setOpen] = useState<boolean>(true);

    const {
        files = [],
        auto_data,
        required_context,
        warnings = [],
    } = json_response || {};

    return (
        <div
            className={`h-screen rounded-lg w-full overflow-auto pn_scroller transition-all duration-300 ease-in-out ${open
                ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_460px] max-w-full lg:max-w-[460px] p-2 border border-gray-300"
                : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
                }`}
        >
            <div className="flex flex-col h-full bg-opacity-50">

                {/* ───────────────── HEADER ───────────────── */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Quant Ingestion Summary</h2>

                    <button
                        onClick={() => setOpen(!open)}
                        className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
                    >
                        <img src={SwitchIcon} alt="Toggle" className="w-3 h-3 object-contain" />
                    </button>
                </div>
                {open &&
                    <>
                        {/* ───────────────── FILES FROM JSON ───────────────── */}
                        {files.length > 0 && (
                            <div className="mb-2">
                                <h3 className="font-medium text-sm mb-2">Parsed Files</h3>

                                {files.map((file: any, i: number) => (
                                    <div
                                        key={i}
                                        className="p-2 mb-2 border border-[#D7D7D7] rounded-md text-sm"
                                    >
                                        <p className="font-semibold">{file.filename}</p>
                                        <p className="text-gray-700">Status: {file.status}</p>
                                        <p className="text-gray-500 text-xs">{file.details}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ───────────────── AUTO DATA ───────────────── */}
                        {auto_data && (
                            <div className="mb-2">
                                <h3 className="font-medium text-sm mb-2">Auto Detected</h3>

                                <div className="text-sm">
                                    <p className="mb-2">
                                        <span className="font-semibold">Sample Size Detected:</span>{" "}
                                        {auto_data.sample_size_detected ? "Yes" : "No"}
                                    </p>

                                    <p className="font-semibold mb-1">Segments Detected:</p>
                                    <div className="flex flex-wrap gap-1">
                                        {auto_data.segments_detected?.map((seg: string, idx: number) => (
                                            <div
                                                key={idx}
                                                className="p-1 rounded bg-gray-100 border text-xs"
                                            >
                                                {seg}
                                            </div>
                                        ))}
                                    </div>

                                    {auto_data.response_types && (
                                        <>
                                            <p className="font-semibold mt-3 mb-1">Response Types:</p>
                                            <div className="flex flex-wrap gap-1">
                                                {auto_data.response_types.map((t: string, i: number) => (
                                                    <div
                                                        key={i}
                                                        className="p-1 rounded bg-blue-50 border text-xs"
                                                    >
                                                        {t}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ───────────────── REQUIRED CONTEXT ───────────────── */}
                        {required_context && (
                            <div className="mb-2">
                                <h3 className="font-medium text-sm mb-2">Required Context</h3>

                                <div className="text-sm space-y-2">
                                    <p>
                                        <span className="font-semibold">Study Topic:</span>{" "}
                                        {required_context.study_topic}
                                    </p>

                                    <p>
                                        <span className="font-semibold">Total Sample Size:</span>{" "}
                                        {required_context.total_n}
                                    </p>

                                    <div>
                                        <p className="font-semibold mb-1">Segments:</p>
                                        <div className="flex flex-wrap gap-1">
                                            {required_context.segments?.map((s: string, idx: number) => (
                                                <div
                                                    key={idx}
                                                    className="p-1 rounded bg-blue-50 border text-xs"
                                                >
                                                    {s}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {required_context.notes && required_context.notes.trim() !== "" && (
                                        <p className="text-xs text-gray-600">Notes: {required_context.notes}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ───────────────── WARNINGS ───────────────── */}
                        {warnings.length > 0 && (
                            <div className="mb-2">
                                <h3 className="font-medium text-sm mb-2 text-red-600">Warnings</h3>

                                {warnings.map((w: any, i: number) => (
                                    <div
                                        key={i}
                                        className="p-2 mb-2 rounded-md border border-red-300 bg-red-50 text-sm"
                                    >
                                        <p className="font-semibold">⚠ {w.message}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>}

            </div>
        </div>
    );
};

export default QuantIngestionPanel;
